import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {reaction} from "mobx";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Slider from "@mui/material/Slider";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import {SequenceApplicationStore} from "./store/SequenceApplicationStore";
import {SequenceStateStore} from "./store/SequenceStateStore";
import {ProjectAssetsStore} from "../state/ProjectAssetsStore";
import {SequenceCompositionService} from "./services/SequenceCompositionService";
import {CompositionEventsEnum} from "../librettoEditor/CompositionEvents";
import {formatDurationBrief} from "../utils/utils";
import {useAssetStore} from "../storage/AssetStore";
import { alpha, useTheme } from "@mui/material/styles";

const Panel: React.FC<{ title: string; right?: React.ReactNode; children: React.ReactNode }> = ({title, right, children}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--app-bg-surface)',
                border: '0.5px solid var(--app-border-subtle)',
                borderRadius: '12px',
                px: 1,
                py: 1,
            }}
        >
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, py: 0.5}}>
                <Typography sx={{fontSize: 13, fontWeight: 600, color: 'var(--app-text-primary)'}}>{title}</Typography>
                {right}
            </Box>
            {/*<Divider sx={{my: 0.5}}/>*/}
            <Box>
                {children}
            </Box>
        </Box>
    );
};

export const SequenceTrackPropertiesBar: React.FC = observer(() => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const selectedClipId = SequenceApplicationStore.selectedClipId;
    const trackId = selectedClipId ? SequenceStateStore.clipIdToAssetId?.[selectedClipId] : undefined;
    const trackRecord: any | undefined = ProjectAssetsStore.projectTracks.find(
        (t: any) => String(t.id) === String(trackId)
    );

    // Get asset store functions for Studio Sound
    const { GetEnhancedAudioUrl, GetTrackUrl } = useAssetStore();

    // Get layerId from the selected clip
    const layerId = useMemo(() => {
        if (!selectedClipId) return null;
        const clip: any = SequenceCompositionService.getClipById(selectedClipId);
        return clip?.track?.id ?? null;
    }, [selectedClipId]);

    // Playback rate
    const [rate, setRate] = useState<number>(1);
    // Volume percentage [0..100]
    const [volumePct, setVolumePct] = useState<number>(100);
    const [isMuted, setIsMuted] = useState<boolean>(false);
    const previousVolumePctRef = useRef<number>(100);
    // Studio Sound toggle
    const [studioSound, setStudioSound] = useState<boolean>(false);
    // Track if Studio Sound toggle is in progress
    const [studioSoundLoading, setStudioSoundLoading] = useState<boolean>(false);
    // Cache original audio URLs to avoid re-fetching
    const originalAudioUrlsRef = useRef<Map<string, string>>(new Map());

    // Get enhanced state for the track (Studio Sound)
    const enhancedState = useMemo(() => {
        if (!trackRecord) return { state: '', isReady: false, isProcessing: false, isFailed: false, isAvailable: false };
        const state = trackRecord.enhancedTrackState || '';
        return {
            state,
            isReady: state === 'ready',
            isProcessing: state === 'processing',
            isFailed: state === 'failed',
            isAvailable: !!state,
        };
    }, [trackRecord]);
    const controlSurfaceBackground = "var(--app-bg-subtle)";
    const controlBorder = "1px solid var(--app-border-subtle)";
    const controlTextColor = "var(--app-text-primary)";
    const mutedTextColor = "var(--app-text-muted)";
    const iconColor = "var(--app-icon-primary)";
    const primaryAccentColor = theme.palette.primary.main;
    const dangerTextColor = isDarkMode ? theme.palette.error.light : theme.palette.error.main;
    const secondaryIconHoverBackground = isDarkMode
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.common.black, 0.04);

    useEffect(() => {
        const d = reaction(
            () => ({
                clipId: SequenceApplicationStore.selectedClipId,
                layerId: layerId,
            }),
            () => {
                const clipId = SequenceApplicationStore.selectedClipId;
                if (!clipId) return;

                // Pull playback rate and gain/volume from clip, if any
                const clip: any = SequenceCompositionService.getClipById(clipId);
                if (clip) {
                    const r = Number(clip.playbackRate ?? 1) || 1;
                    setRate(r);
                    const g = (typeof clip.volume === 'number') ? Number(clip.volume) : (typeof clip.gain === 'number' ? Number(clip.gain) : 1);
                    const nextVolumePct = Math.max(0, Math.min(100, Math.round((g ?? 1) * 100)));
                    setVolumePct(nextVolumePct);
                    setIsMuted(nextVolumePct <= 0);
                    if (nextVolumePct > 0) {
                        previousVolumePctRef.current = nextVolumePct;
                    }
                    // Initialize Studio Sound state from clip
                    if (typeof clip.studioSoundEnabled === 'boolean') {
                        setStudioSound(clip.studioSoundEnabled);
                    } else {
                        setStudioSound(false);
                    }
                }
            },
            {fireImmediately: true}
        );
        return () => d();
    }, [layerId]);

    const durationSec = (() => {
        if (!selectedClipId) return 0;
        return SequenceStateStore.clips?.[selectedClipId]?.trimmedDuration ?? 0;
    })();

    const applyPlaybackRate = (value: number) => {
        const clip: any = selectedClipId ? SequenceCompositionService.getClipById(selectedClipId) : null;
        if (!clip) return;
        try {
            clip.playbackRate = value;
            // notify UI timelines
            (SequenceCompositionService as any)?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, {clip});
        } catch {}
    };

    const applyVolume = (pct: number) => {
        const clip: any = selectedClipId ? SequenceCompositionService.getClipById(selectedClipId) : null;
        if (!clip) return;
        const boundedPct = Math.max(0, Math.min(100, Number(pct) || 0));
        const linear = boundedPct / 100;
        try {
            let usedVolumeSetter = false;
            if (typeof clip.volume === 'number') {
                clip.volume = linear;
                usedVolumeSetter = true;
            } else if (typeof clip.gain === 'number' || clip.gain === undefined) {
                clip.gain = linear;
            }
            if (!usedVolumeSetter) {
                try { (SequenceCompositionService as any)?.composition?.onClipTimingChanged?.(); } catch {}
            }
            (SequenceCompositionService as any)?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, {clip});
        } catch {}
        setVolumePct(boundedPct);
        setIsMuted(boundedPct <= 0);
        if (boundedPct > 0) {
            previousVolumePctRef.current = boundedPct;
        }
    };

    const handleToggleMute = () => {
        if (isMuted || volumePct <= 0) {
            const restoredVolume = Math.max(1, Math.min(100, Math.round(previousVolumePctRef.current || 100)));
            applyVolume(restoredVolume);
            return;
        }
        if (volumePct > 0) {
            previousVolumePctRef.current = volumePct;
        }
        applyVolume(0);
    };

    const handleDelete = () => {
        if (!selectedClipId) return;
        SequenceCompositionService.deleteClipById(selectedClipId);
        SequenceApplicationStore.setSelectedLibrettoClipIdToNull();
    };

    const handleStudioSoundToggle = useCallback(async (enabled: boolean) => {
        if (!selectedClipId || !trackId) return;

        const clip: any = SequenceCompositionService.getClipById(selectedClipId);
        if (!clip || typeof clip.setStudioSound !== 'function') {
            console.warn('[SequenceTrackPropertiesBar] Clip does not support setStudioSound');
            return;
        }

        // Check if enhanced audio is ready
        if (enabled && !enhancedState.isReady) {
            console.warn('[SequenceTrackPropertiesBar] Enhanced audio not ready');
            return;
        }

        setStudioSoundLoading(true);
        try {
            // Get enhanced URL using asset store (same as V2PropertiesSidebar)
            const enhancedUrl = enabled ? await GetEnhancedAudioUrl({ trackId }) : undefined;
            if (enabled && !enhancedUrl) {
                console.warn('[SequenceTrackPropertiesBar] Enhanced audio URL not available');
                setStudioSoundLoading(false);
                return;
            }

            // Get/cache original URL
            let originalUrl = originalAudioUrlsRef.current.get(selectedClipId);
            if (!originalUrl) {
                originalUrl = await GetTrackUrl({ trackId });
                if (originalUrl) {
                    originalAudioUrlsRef.current.set(selectedClipId, originalUrl);
                }
            }

            await clip.setStudioSound(enabled, enhancedUrl, originalUrl);
            setStudioSound(enabled);
        } catch (err) {
            console.error('[SequenceTrackPropertiesBar] Failed to toggle Studio Sound:', err);
        } finally {
            setStudioSoundLoading(false);
        }
    }, [selectedClipId, trackId, enhancedState.isReady, GetEnhancedAudioUrl, GetTrackUrl]);

    if (!selectedClipId || !layerId) {
        return null;
    }

    return (
        <Box sx={{height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', p: '3px'}}>
            {/* Playback section */}
            <Panel title="Duration" right={null}>
                <Box sx={{display: 'flex', gap: 1, px: 1, py: 0.5}}>
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        background: controlSurfaceBackground,
                        border: controlBorder,
                        borderRadius: '8px',
                        px: 1,
                        height: 30
                    }}>
                        <Select
                            value={rate}
                            size="small"
                            onChange={(e) => {
                                const v = Number(e.target.value);
                                setRate(v);
                                applyPlaybackRate(v);
                            }}
                            displayEmpty
                            sx={{
                                fontSize: 13,
                                '& .MuiSelect-select': {py: 0.5},
                                background: 'var(--app-bg-surface)',
                                borderRadius: '6px',
                                color: controlTextColor,
                                '& .MuiSvgIcon-root': { color: iconColor },
                                '& fieldset': { borderColor: 'var(--app-border-subtle)' },
                                '&:hover fieldset': { borderColor: 'var(--app-border-muted)' },
                            }}
                        >
                            {[1, 1.25, 1.5, 1.75, 2, 2.5, 3].map((r) => (
                                <MenuItem key={r} value={r} sx={{fontSize: 12, color: controlTextColor}}>{r}x</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: controlSurfaceBackground,
                        border: controlBorder,
                        borderRadius: '8px',
                        px: 1,
                        height: 30,
                        fontSize: 13,
                        color: controlTextColor,
                    }}>
                        {formatDurationBrief(durationSec || 0)}
                    </Box>
                </Box>
            </Panel>

            {/* Audio section */}
            <Panel title="Audio" right={
                <IconButton size="small" sx={{ color: iconColor, '&:hover': { backgroundColor: secondaryIconHoverBackground } }}>
                    <MoreVertIcon fontSize="small"/>
                </IconButton>
            }>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5}}>
                    <Box sx={{
                        width: 75,
                        background: controlSurfaceBackground,
                        border: controlBorder,
                        borderRadius: '8px',
                        textAlign: 'center',
                        height: 30,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        color: controlTextColor,
                    }}>
                        {volumePct}%
                    </Box>
                    <Box sx={{flex: 1, px: 1}}>
                        <Slider
                            size="small"
                            value={volumePct}
                            color={"info"}
                            onChange={(_, v) => {
                                const nextValue = Array.isArray(v) ? Number(v[0]) : Number(v);
                                const bounded = Math.max(0, Math.min(100, Number.isFinite(nextValue) ? nextValue : 0));
                                setVolumePct(bounded);
                                setIsMuted(bounded <= 0);
                                if (bounded > 0) {
                                    previousVolumePctRef.current = bounded;
                                }
                            }}
                            onChangeCommitted={(_, v) => {
                                const nextValue = Array.isArray(v) ? Number(v[0]) : Number(v);
                                applyVolume(nextValue);
                            }}
                            min={0}
                            max={100}
                            sx={{
                                color: primaryAccentColor,
                                '& .MuiSlider-rail': {
                                    backgroundColor: 'var(--app-border-muted)',
                                    opacity: 1,
                                },
                                '& .MuiSlider-track': {
                                    border: 'none',
                                },
                                '& .MuiSlider-thumb': {
                                    backgroundColor: 'var(--app-bg-surface)',
                                    border: `2px solid ${primaryAccentColor}`,
                                },
                            }}
                        />
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleToggleMute}
                        sx={{color: iconColor, '&:hover': { backgroundColor: secondaryIconHoverBackground }}}
                    >
                        {isMuted ? <VolumeOffOutlinedIcon fontSize="small" /> : <VolumeUpOutlinedIcon fontSize="small" />}
                    </IconButton>
                </Box>
            </Panel>

            {/* Audio effects - only show when enhanced audio is available */}
            {enhancedState.isAvailable && (
                <Panel title="Audio effects" right={
                    <IconButton size="small" sx={{ color: iconColor, '&:hover': { backgroundColor: secondaryIconHoverBackground } }}>
                        <AddIcon fontSize="small"/>
                    </IconButton>
                }>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5}}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{fontSize: 13, color: controlTextColor}}>Studio Sound</Typography>
                            {enhancedState.isProcessing && (
                                <CircularProgress size={12} sx={{ color: mutedTextColor }} />
                            )}
                        </Box>
                        <Switch
                            checked={studioSound}
                            onChange={(e) => handleStudioSoundToggle(e.target.checked)}
                            disabled={!enhancedState.isReady || studioSoundLoading}
                            size="small"
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: primaryAccentColor,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: primaryAccentColor,
                                },
                                '& .MuiSwitch-track': {
                                    backgroundColor: 'var(--app-border-muted)',
                                },
                            }}
                        />
                    </Box>
                    {enhancedState.isProcessing && (
                        <Typography sx={{ fontSize: 11, color: mutedTextColor, px: 1, pb: 0.5 }}>
                            Processing audio...
                        </Typography>
                    )}
                    {enhancedState.isFailed && (
                        <Typography sx={{ fontSize: 11, color: dangerTextColor, px: 1, pb: 0.5 }}>
                            Processing failed
                        </Typography>
                    )}
                </Panel>
            )}

            {/* Delete track */}
            <Box sx={{
                background: 'var(--app-bg-surface)',
                border: '0.5px solid var(--app-border-subtle)',
                borderRadius: '12px',
                // px: 1,
            }}>
                <Button
                    variant="text"
                    onClick={handleDelete}
                    fullWidth
                    sx={{
                        height: 35,
                        borderRadius: '8px',
                        textTransform: 'none',
                        color: controlTextColor,
                        '&:hover': {
                            backgroundColor: secondaryIconHoverBackground,
                        },
                    }}
                >
                    Delete track
                </Button>
            </Box>
        </Box>
    );
});

export default SequenceTrackPropertiesBar;
